import { useMemo } from 'react';

import { OverflowText } from '@/components/OverflowText';
import { getColumnWidth } from '@/components/table/columns/registry';
import { TanStackTable } from '@/components/table/TanStackTable';
import { Columns } from '@/components/table/types';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { SettingDisplayCard } from '@/sections/settings/components';
import { useAuth } from '@/state/auth';
import { Access } from '@/types';
import { formatRoleName } from '@/utils/role.util';
import { isPraetorianEmail } from '@/utils/userDisplay.util';

interface AccessLogsCardsProps {
  /**
   * Email for impersonation - if provided, will fetch data for this user
   * If not provided, uses the current authenticated user
   */
  email?: string;
  /**
   * Whether the card is editable (disabled during impersonation)
   * Note: Access logs are read-only, so this prop is for consistency with other settings cards
   */
  editable?: boolean;
}

/**
 * AccessLogsCards displays access logs for the account.
 *
 * Shows a table of users who have accessed the account, including:
 * - User email
 * - Access type (SSO or Username/Password)
 * - SSO Domain (if applicable)
 * - Last activity timestamp
 *
 * Features:
 * - Sorts logs by updated date (most recent first)
 * - Filters out Praetorian emails for non-Praetorian users
 * - Only renders when access logs exist
 * - Supports impersonation via email prop
 *
 * React 19 Features:
 * - Standard caching strategy with TanStack Query
 * - No unnecessary memoization (React Compiler handles optimization)
 *
 * @example
 * ```tsx
 * // In AccessLogsTab
 * <AccessLogsCards email={isImpersonating ? friend : undefined} />
 * ```
 *
 * @see {@link UserSettingsCards} - Pattern reference
 */
export function AccessLogsCards({
  email,
  editable: _editable = true,
}: AccessLogsCardsProps) {
  const { me, friend, userType } = useAuth();
  const enableRbac = useRbacEnabled();
  const currentEmail = email || friend || me;

  // Access logs data
  const {
    data: accessGeneric,
    error: accessError,
    status: accessStatus,
  } = useGenericSearch(
    {
      query: '#access',
    },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  // Sort access logs by updated date (most recent first)
  const accessLogs = useMemo(() => {
    return (accessGeneric?.accesss || []).sort((a, b) => {
      return new Date(b.updated).getTime() - new Date(a.updated).getTime();
    });
  }, [accessGeneric]);

  // Filter access logs based on user type
  // Praetorian users see all logs, customers see only customer logs
  const filteredAccessLogs = useMemo(() => {
    return userType === 'praetorian'
      ? accessLogs
      : accessLogs?.filter(log => !isPraetorianEmail(log.email));
  }, [accessLogs, userType]);

  // Don't render if no access logs exist
  if (!filteredAccessLogs || filteredAccessLogs.length === 0) {
    return null;
  }

  return (
    <SettingDisplayCard data-testid="access-logs-card">
      <div className="flex w-full flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h4 className="font-bold">Access Logs</h4>
          <span className="text-chariot-text-secondary text-xs">
            These are the users who have accessed your account.
          </span>
        </div>

        {/* Access Logs Table */}
        <div className="border-chariot-stroke overflow-hidden rounded-md border">
          <TanStackTable<Access>
            name="access-logs"
            columns={
              [
                {
                  label: 'User',
                  id: 'email',
                  ...getColumnWidth('access', 'email'),
                  cell: (access: Access) => (
                    <OverflowText text={access.email} />
                  ),
                },
                {
                  label: 'Access Type',
                  id: 'type',
                  cell: (access: Access) =>
                    access.type === 'sso' ? 'SSO' : 'Username/Password',
                  ...getColumnWidth('access', 'type'),
                },
                ...(enableRbac
                  ? ([
                      {
                        label: 'Role',
                        id: 'role',
                        cell: (access: Access) => formatRoleName(access.role),
                        ...getColumnWidth('access', 'role'),
                      },
                    ] as Columns<Access>)
                  : []),
                {
                  label: 'SSO Domain',
                  id: 'value',
                  cell: (access: Access) =>
                    access.type === 'sso' ? access.value : '-',
                  ...getColumnWidth('access', 'value'),
                },
                {
                  label: 'Last Activity',
                  id: 'updated',
                  cell: 'date',
                  ...getColumnWidth('shared', 'visited'),
                },
              ] as Columns<Access>
            }
            data={filteredAccessLogs}
            error={accessError}
            loadingRowCount={3}
            status={accessStatus}
            noData={{
              description: 'No access logs found.',
            }}
            selection={{
              hide: true,
            }}
          />
        </div>
      </div>
    </SettingDisplayCard>
  );
}
